// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Complaints & refund assistance  (API for pages/Complaint.tsx and pages/RefundAssistance.tsx)
// A "claim" (shown to customers as a complaint / problem report) is raised for a DELIVERED order (POST /api/me/orders/{ref}/claim,
// one per order, only within `windowHours` of delivery), optionally naming the affected items. ZenFresh staff review it — nothing is
// automatic — and an approved claim is paid as wallet credit (never more than the order total). GET /api/me/claims lists them with
// their status. Backend: `claims/{orderRef}`.
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed } from "./api";

export type ClaimReason = "not_fresh" | "wrong_weight" | "wrong_cut" | "damaged_packaging" | "missing_item" | "late_delivery" | "other";
export type ClaimStatus = "submitted" | "approved" | "rejected";

export interface Claim {
  orderRef: string;
  reason: ClaimReason;
  note: string | null;
  status: ClaimStatus;
  createdAt: string;
  orderTotal: number;
  resolvedAt: string | null;
  creditInr: number;
  resolutionNote: string | null;
  /** Catch ids the complaint is about; empty = the whole order. */
  items: string[];
}

export interface ClaimList {
  /** How long after delivery a problem can be reported. */
  windowHours: number;
  claims: Claim[];
}

export const MAX_NOTE = 500;

/** The reasons offered, in the words shown to customers. */
export const CLAIM_REASONS: { id: ClaimReason; label: string; hint: string }[] = [
  { id: "wrong_cut", label: "Cutting or cleaning error", hint: "Wrong cut style, bones or scales left" },
  { id: "not_fresh", label: "Freshness or smell concern", hint: "Soft flesh, cloudy eyes, off smell" },
  { id: "wrong_weight", label: "Less than ordered", hint: "Pack lighter than its size" },
  { id: "damaged_packaging", label: "Damaged packaging", hint: "Seal broken or leaking" },
  { id: "missing_item", label: "Item missing", hint: "Something on the order didn't arrive" },
  { id: "late_delivery", label: "Delivery was late", hint: "It arrived later than expected" },
  { id: "other", label: "Something else", hint: "Tell us in the description" },
];

export const reasonLabel = (reason: ClaimReason) => CLAIM_REASONS.find((r) => r.id === reason)?.label ?? "Problem reported";

export const fetchClaims = () => apiAuthed<ClaimList>("GET", "/me/claims");

export const submitClaim = (orderRef: string, reason: ClaimReason, note: string, items: string[] = []) =>
  apiAuthed<Claim>("POST", `/me/orders/${orderRef}/claim`, { reason, items, ...(note.trim() ? { note: note.trim() } : {}) });
